import { useContext, useMemo, useCallback } from "react";
import { v4 as uuid } from "uuid";
import { API_DATE_TIME_FORMAT } from "api/date";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { selectSystem, selectUser } from "module/auth/redux/authSlice";
import usePermissions from "module/permission/usePermissions";
import { MaintenanceTaskInterface } from "module/maintenanceTask/types";
import MaintenanceTaskContext from "module/maintenanceTask/context/MaintenanceTaskContext";
import MaintenanceTaskOpenCloseContext from "module/maintenanceTask/openClose/context/MaintenanceTaskOpenCloseContext";
import {
  addToEntityPropertyList,
  addToMyEntities,
} from "module/staff/redux/staffDataSlice";
import {
  AttachmentInterface,
  MyAttachmentInterface,
} from "module/attachment/types";
import { EntityType } from "module/entity/types";
import { MaintenanceTaskProcessStepInterface } from "module/maintenanceTaskProcessStep/types";
import { canAddMaintenanceTaskProcessStepAttachment } from "module/maintenanceTaskProcessStepAttachment/helper/canAddMaintenanceTaskProcessStepAttachment";
import MaintenanceTaskProcessStepContext from "module/maintenanceTaskProcessStep/context/MaintenanceTaskProcessStepContext";

interface Result {
  canAdd: boolean;
  add: (mimeType: string, base64: string) => void;
}

const useMaintenanceTaskProcessStepAttachmentAdd = (): Result => {
  const { entity: maintenanceTaskEntity } = useContext(MaintenanceTaskContext);
  const maintenanceTask = maintenanceTaskEntity as MaintenanceTaskInterface;

  const { entity: processStepEntity } = useContext(
    MaintenanceTaskProcessStepContext
  );
  const maintenanceTaskProcessStep =
    processStepEntity as MaintenanceTaskProcessStepInterface;

  const { isOpen } = useContext(MaintenanceTaskOpenCloseContext);

  const dispatch = useAppDispatch();
  const staffId = useAppSelector(selectUser).id;
  const systemShortName = useAppSelector(selectSystem).shortName;

  const { canEditMaintenanceTask } = usePermissions();

  const canAdd = useMemo(
    () =>
      canAddMaintenanceTaskProcessStepAttachment({
        isMaintenanceTaskOpen: isOpen,
        maintenanceTask,
        canEditMaintenanceTask,
      }),
    [canEditMaintenanceTask, isOpen, maintenanceTask]
  );

  const add = useCallback(
    (mimeType: string, base64: string) => {
      const maintenanceTaskProcessStepEntityIdentifier = {
        id: maintenanceTaskProcessStep.id,
        type: EntityType.maintenanceTaskProcessStep,
      };

      const id = uuid();
      const entityIdentifier = {
        type: EntityType.attachment,
        id,
      };

      const today = moment()
        .set({ hour: 0, minute: 0, second: 0 })
        .format(API_DATE_TIME_FORMAT);

      const data: AttachmentInterface = {
        id,
        base64,
        mimeType,
        relationships: undefined,
        relationshipIdentifiers: [],
        changedDateTime: today,
      };

      const myAttachment: MyAttachmentInterface = {
        entityIdentifier,
        data,
        dataWithoutChanges: data,
        validationErrors: null,
        validationWarnings: null,
      };

      dispatch(
        addToMyEntities({
          staffId,
          systemShortName,
          entities: [myAttachment],
        })
      );

      dispatch(
        addToEntityPropertyList({
          staffId,
          systemShortName,
          baseEntityIdentifier: maintenanceTaskProcessStepEntityIdentifier,
          propertyName: "attachments",
          entityIdentifierToAdd: entityIdentifier,
        })
      );
    },
    [dispatch, maintenanceTaskProcessStep.id, staffId, systemShortName]
  );

  return {
    canAdd,
    add,
  };
};

export default useMaintenanceTaskProcessStepAttachmentAdd;
